/**
 * FormRepeatableRow — renders a single row in repeatable mode.
 * Runs per-row scoped effects and observers with prefixed field paths.
 * Each row is wrapped in a bordered Surface card with a row number badge.
 */

import React, { useMemo } from 'react';
import { Surface } from '@packages/ui/layout';
import { Stack } from '@packages/ui/layout';
import { Button } from '@packages/ui/primitives/Button';
import { Lozenge } from '@packages/ui/primitives/Lozenge';
import { FormNodeRenderer } from './FormNodeRenderer';
import { useEffectProcessor } from '../processors/effectProcessor';
import { useObserverProcessor } from '../processors/observerProcessor';
import { prefixEffects, prefixObservers } from '../processors/pathRewriter';
import { useFormEngineForm, useFormEngineState } from '../context/FormEngineContext';
import type { FormLayoutNode } from '../types/layout';
import type { FormEffect, FormObserver } from '../types/block';
import type { RepeatableCardConfig } from '../types/schema';
import type { FormPrecisionConfig } from '../precision/precisionContract';

interface FormRepeatableRowProps {
  index: number;
  layout: FormLayoutNode[];
  effects: FormEffect[];
  observers: FormObserver[];
  precisionConfig?: FormPrecisionConfig;
  canRemove: boolean;
  onRemove: (index: number) => void;
  rowLabel?: string;
  cardConfig?: RepeatableCardConfig;
}

export const FormRepeatableRow = React.memo(function FormRepeatableRow({
  index,
  layout,
  effects,
  observers,
  precisionConfig,
  canRemove,
  onRemove,
  rowLabel,
  cardConfig,
}: FormRepeatableRowProps): React.ReactElement {
  const { form, programmaticWriteGuard } = useFormEngineForm();
  const { meta, getOptionProp } = useFormEngineState();

  const rowPrefix = `items.${index}`;

  const scopedEffects = useMemo(
    () => prefixEffects(effects, rowPrefix),
    [effects, rowPrefix],
  );

  const scopedObservers = useMemo(
    () => prefixObservers(observers, rowPrefix),
    [observers, rowPrefix],
  );

  useEffectProcessor({
    form: form as any,
    effects: scopedEffects,
    meta,
    getOptionProp,
    precisionConfig,
    programmaticWriteGuard,
  });

  useObserverProcessor({
    form: form as any,
    observers: scopedObservers,
    meta,
    getOptionProp,
    precisionConfig,
  });

  const label = formatRowLabel(cardConfig?.labelTemplate, index, rowLabel);
  const badgeVariant = cardConfig?.badgeVariant ?? 'info';
  const removeLabel = cardConfig?.removeLabel ?? 'Delete';
  const removeVariant = cardConfig?.removeVariant ?? 'ghost';

  return (
    <Surface bordered padding='0'>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 12px 0 8px' }}>
        <Lozenge variant={badgeVariant} title={label} />
        {canRemove && (
          <Button
            variant={removeVariant}
            size='sm'
            icon='delete'
            semanticId={`removeRow-${index}`}
            onClick={() => onRemove(index)}
            style={{ color: 'var(--ui-status-danger)' }}
          >
            {removeLabel}
          </Button>
        )}
      </div>
      <div style={{ padding: '12px 12px 16px 24px' }}>
        <Stack gap='20px'>
          {layout.map((node, nodeIdx) => (
            <FormNodeRenderer
              key={nodeIdx}
              node={node}
              namePrefix={rowPrefix}
            />
          ))}
        </Stack>
      </div>
    </Surface>
  );
});

function formatRowLabel(
  template: string | undefined,
  index: number,
  rowLabel?: string,
): string {
  const resolvedTemplate = template ?? (rowLabel ? '{label} #{n}' : '#{n}');
  return resolvedTemplate
    .replace(/\{n\}/g, String(index + 1))
    .replace(/\{label\}/g, rowLabel ?? '');
}
